/**
 * EmailVerificationBanner.jsx - Email Verification Notification Banner
 * 
 * @description
 * Displays a notification banner when user's email is not verified.
 * Allows users to resend verification emails.
 * 
 * @features
 * - Shows verification status
 * - Resend verification email
 * - Auto-hide after verification
 * - Loading states and error handling
 */

import { useState, useEffect } from 'react';
import { checkVerificationStatus, resendVerificationEmail, resendVerificationByEmail } from '../services/emailService';
import { useToast } from './Toast';

const EmailVerificationBanner = ({ user, onVerificationChange }) => {
  const [isVerified, setIsVerified] = useState(user?.email_verified || false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (user && !user.email_verified) {
      checkVerificationStatus()
        .then(response => {
          setIsVerified(response.verified);
          if (onVerificationChange) {
            onVerificationChange(response.verified);
          }
        })
        .catch(error => {
          console.warn('Could not check verification status:', error);
        });
    }
  }, [user, onVerificationChange]);

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      // First try the authenticated endpoint
      await resendVerificationEmail();
      showSuccess('Verification email sent! Check your inbox.');
    } catch (error) {
      // If JWT fails, try fallback with email
      if (error.message.includes('Please provide your email') || error.message.includes('401')) {
        try {
          // Use user email for fallback
          if (user && user.email) {
            await resendVerificationByEmail(user.email);
            showSuccess('Verification email sent! Check your inbox.');
          } else {
            showError('Please log in again to resend verification email');
          }
        } catch (fallbackError) {
          showError(fallbackError.message || 'Failed to send verification email');
        }
      } else {
        showError(error.message || 'Failed to send verification email');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Don't show banner if user is verified or no user
  if (!user || isVerified) {
    return null;
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700 dark:text-yellow-200">
              <strong>Email verification required:</strong> Please check your inbox and click the verification link to activate your account.
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={handleResendVerification}
            disabled={isResending}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-700 dark:text-yellow-200" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              'Resend Email'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
